import { AccountAddress } from '@aptos-labs/ts-sdk';
import type { AccountInfo } from '@aptos-labs/wallet-adapter-react';
import {
	aptosClient,
	resolveVaultConfig,
	TOKEN_DECIMALS,
	VAULT_ERRORS,
	VaultConfig,
} from './constants';
import { Position } from './vaultTypes';
import * as Sentry from '@sentry/react';

export const convertToBaseUnits = (usdcAmount: number): number => {
	return Math.floor(usdcAmount * Math.pow(10, TOKEN_DECIMALS.USDC));
};

export const convertFromBaseUnits = (baseUnits: number): number => {
	return baseUnits / Math.pow(10, TOKEN_DECIMALS.USDC);
};

export const formatUSDC = (baseUnits: number): string => {
	const usdc = convertFromBaseUnits(baseUnits);
	return usdc.toLocaleString(undefined, {
		minimumFractionDigits: 1,
		maximumFractionDigits: 2,
	});
};

export const parseVaultError = (error: any): string => {
	// Handle different error formats
	let errorString = '';
	let errorMessage = '';

	try {
		// Try to get error message from various possible locations
		errorMessage =
			error?.message ||
			error?.error?.message ||
			error?.data?.message ||
			error?.toString() ||
			'';
		errorString = JSON.stringify(error);
	} catch (e) {
		// If JSON.stringify fails, fall back to string conversion
		errorString = String(error);
		errorMessage = error?.message || errorString;
	}

	// Check for rate limiting
	if (
		errorString.includes('429') ||
		errorString.includes('Too Many Requests') ||
		errorString.includes('rate limit')
	) {
		return 'Rate limit reached. Please wait a moment and try again.';
	}

	// Try to extract error code
	const errorCodeMatch = errorString.match(/abort.*?(\d+)/i);
	if (errorCodeMatch) {
		const code = parseInt(errorCodeMatch[1]);
		if (VAULT_ERRORS[code]) {
			return VAULT_ERRORS[code];
		}
	}

	// Common error patterns
	if (
		errorString.includes('INSUFFICIENT_BALANCE') ||
		errorString.includes('insufficient')
	) {
		return 'Insufficient balance for this transaction';
	}
	if (errorString.includes('SEQUENCE_NUMBER')) {
		return 'Transaction sequence error. Please try again.';
	}
	if (errorString.includes('timeout') || errorString.includes('deadline')) {
		return 'Transaction timed out. It may still be processing.';
	}

	// Return clean error message
	return errorMessage || 'Transaction failed. Please try again.';
};

export const getAptosBalance = async (
	accountAddress: AccountAddress,
): Promise<number> => {
	try {
		try {
			const [octasStr] = await aptosClient.view<[string]>({
				payload: {
					function: '0x1::coin::balance',
					typeArguments: ['0x1::aptos_coin::AptosCoin'],
					functionArguments: [accountAddress.toString()],
				},
			});

			const octas = BigInt(octasStr ?? '0');
			return parseFloat((Number(octas) / 1e8).toFixed(2));
		} catch (viewError) {
			type CoinStore = { coin: { value: string } };
			const resource = await aptosClient.getAccountResource<CoinStore>({
				accountAddress,
				resourceType:
					'0x1::coin::CoinStore<0x1::aptos_coin::AptosCoin>',
			});

			const octas = BigInt(resource.coin.value ?? '0');
			return parseFloat((Number(octas) / 1e8).toFixed(2));
		}
	} catch (error) {
		Sentry.captureException(error, {
			tags: { operation: 'getAptosBalance' },
			contexts: { account: { address: accountAddress.toString() } },
		});
		return 0;
	}
};

export const getUSDCBalance = async (
	accountAddress: AccountAddress,
	vaultConfig?: Partial<VaultConfig>,
): Promise<number> => {
	const config = resolveVaultConfig(vaultConfig);
	try {
		// Method 1: Try using primary_fungible_store::balance view function
		try {
			const balance = await aptosClient.view({
				payload: {
					function: '0x1::primary_fungible_store::balance',
					typeArguments: ['0x1::fungible_asset::Metadata'],
					functionArguments: [
						accountAddress.toString(),
						config.USDC_FA_METADATA,
					],
				},
			});

			const balanceNum = parseInt(balance[0] as string);

			return balanceNum;
		} catch (viewError: any) {
			// Method 2: Fallback - scan all fungible asset stores
			const resources = await aptosClient.getAccountResources({
				accountAddress,
			});

			// Look for fungible stores
			const fungibleStores = resources.filter((r: any) =>
				r.type.includes('0x1::fungible_asset::FungibleStore'),
			);

			// Find the USDC store
			for (const store of fungibleStores) {
				const storeData = store.data as any;
				const metadata =
					storeData?.metadata?.inner || storeData?.metadata;
				const balance = storeData?.balance;

				// Check if this store's metadata matches USDC
				if (metadata && metadata === config.USDC_FA_METADATA) {
					const balanceNum = parseInt(balance || '0');

					return balanceNum;
				}
			}

			Sentry.logger.warn('No USDC fungible asset found', {
				accountAddress: accountAddress.toString(),
			});
			return 0;
		}
	} catch (error) {
		Sentry.captureException(error, {
			tags: { operation: 'getUSDCBalance' },
			contexts: { account: { address: accountAddress.toString() } },
		});
		return 0;
	}
};

/**
 * Get vault resource account address
 */
export const getVaultAddress = async (
	vaultConfig?: Partial<VaultConfig>,
): Promise<string> => {
	const config = resolveVaultConfig(vaultConfig);
	try {
		const result = await aptosClient.view({
			payload: {
				function: `${config.CONTRACT_ADDRESS}::${config.MODULE_NAME}::vault_address`,
				typeArguments: [],
				functionArguments: [],
			},
		});

		return result[0] as string;
	} catch (error) {
		Sentry.captureException(error, {
			tags: { operation: 'getVaultAddress' },
		});
		throw error;
	}
};

/**
 * Get next position ID (global counter)
 * This is the ID that will be assigned to the next deposit
 */
export const getNextPositionId = async (
	vaultConfig?: Partial<VaultConfig>,
): Promise<number> => {
	const config = resolveVaultConfig(vaultConfig);
	try {
		const result = await aptosClient.view({
			payload: {
				function: `${config.CONTRACT_ADDRESS}::${config.MODULE_NAME}::next_pos_id`,
				typeArguments: [],
				functionArguments: [],
			},
		});

		return parseInt(result[0] as string);
	} catch (error) {
		Sentry.captureException(error, {
			tags: { operation: 'getNextPositionId' },
		});
		return 0;
	}
};

/**
 * Get position details
 * Returns null if position doesn't exist or was withdrawn
 */
export const getPosition = async (
	posId: number,
	vaultConfig?: Partial<VaultConfig>,
): Promise<Position | null> => {
	const config = resolveVaultConfig(vaultConfig);
	try {
		const result = await aptosClient.view({
			payload: {
				function: `${config.CONTRACT_ADDRESS}::${config.MODULE_NAME}::get_position`,
				typeArguments: [],
				functionArguments: [posId.toString()],
			},
		});

		// Result is tuple: (user_address, vault_balance, credited, finalized)
		const user = result[0] as string;
		const vault_balance = parseInt(result[1] as string);
		const credited = parseInt(result[2] as string);
		const finalized = result[3] as boolean;

		// Check if position was deleted (user == vault_address means deleted)
		const vaultAddr = await getVaultAddress(config);
		if (user === vaultAddr || user === config.CONTRACT_ADDRESS) {
			return null;
		}

		return {
			pos_id: posId,
			user,
			deposited: vault_balance, // Initial assumption, will be tracked separately
			vault_balance,
			credited,
			finalized,
		};
	} catch (error) {
		Sentry.captureException(error, {
			tags: { operation: 'getPosition' },
			contexts: { position: { posId } },
		});
		return null;
	}
};

/**
 * Validate deposit amount
 */
export const validateDepositAmount = (
	amount: number,
	userBalance: number,
	vaultConfig?: Partial<VaultConfig>,
): { valid: boolean; error?: string } => {
	const config = resolveVaultConfig(vaultConfig);
	if (amount < config.MIN_DEPOSIT_USDC) {
		return {
			valid: false,
			error: `Minimum deposit is ${config.MIN_DEPOSIT_USDC} USDC`,
		};
	}

	if (amount > convertFromBaseUnits(userBalance)) {
		return {
			valid: false,
			error: `Insufficient USDC balance. You have ${formatUSDC(
				userBalance,
			)} USDC`,
		};
	}

	return { valid: true };
};

/**
 * Estimate gas for a transaction type
 */
export const estimateGas = (
	type: 'deposit' | 'withdraw' | 'cancel',
): number => {
	// Estimates in octas (1 APT = 100_000_000 octas)
	const estimates = {
		deposit: 500_000, // ~0.005 APT
		withdraw: 300_000, // ~0.003 APT
		cancel: 300_000, // ~0.003 APT
	};

	return estimates[type];
};

// ==================== TRANSACTION FUNCTIONS ====================

/**
 * Deposit USDC to vault using wallet adapter
 */
export const depositToVault = async (
	account: AccountInfo,
	amountUSDC: number,
	signAndSubmitFn: (payload: any) => Promise<{ hash: string }>,
	vaultConfig?: Partial<VaultConfig>,
): Promise<string> => {
	const config = resolveVaultConfig(vaultConfig);
	try {
		const amountBase = convertToBaseUnits(amountUSDC);

		/* console.log(
			'🚀 [Safari Fix] About to call signAndSubmitFn - popup should open NOW',
		);*/

		// Use wallet adapter transaction format
		const transaction = {
			data: {
				function:
					`${config.CONTRACT_ADDRESS}::${config.MODULE_NAME}::deposit` as `${string}::${string}::${string}`,
				typeArguments: [],
				functionArguments: [amountBase.toString()],
			},
		};

		// CRITICAL: Direct call with no Sentry wrapper to avoid async delays
		const response = await signAndSubmitFn(transaction);
		// console.log('✅ [Safari Fix] Transaction submitted:', response.hash);

		const executedTransaction = await aptosClient.waitForTransaction({
			transactionHash: response.hash,
		});

		// Send Sentry data asynchronously after transaction
		Sentry.startSpan(
			{
				op: 'transaction.submit',
				name: 'depositToVault',
			},
			(span) => {
				span.setAttribute('amountUSDC', amountUSDC);
				span.setAttribute('amountBase', amountBase);
				span.setAttribute('txHash', response.hash);
				span.setAttribute('success', executedTransaction.success);
			},
		);

		return executedTransaction.hash;
	} catch (error: any) {
		const parsedError = parseVaultError(error);
		Sentry.captureException(error, {
			tags: { operation: 'depositToVault' },
			contexts: {
				transaction: {
					amountUSDC,
					message: error?.message,
					status: error?.status,
					statusText: error?.statusText,
					parsedError,
				},
			},
		});
		throw new Error(parsedError);
	}
};

/**
 * Withdraw finalized position using wallet adapter
 */
export const withdrawPosition = async (
	posId: number,
	signAndSubmitFn: (payload: any) => Promise<{ hash: string }>,
	vaultConfig?: Partial<VaultConfig>,
): Promise<string> => {
	const config = resolveVaultConfig(vaultConfig);
	try {
		/* console.log(
			'🚀 [Safari Fix] About to withdraw - popup should open NOW',
		);*/

		const transaction = {
			data: {
				function:
					`${config.CONTRACT_ADDRESS}::${config.MODULE_NAME}::withdraw_position` as `${string}::${string}::${string}`,
				typeArguments: [],
				functionArguments: [posId.toString()],
			},
		};

		// CRITICAL: Direct call with no Sentry wrapper to avoid async delays
		const response = await signAndSubmitFn(transaction);
		/* console.log(
			'✅ [Safari Fix] Withdraw transaction submitted:',
			response.hash,
		);*/

		const executedTransaction = await aptosClient.waitForTransaction({
			transactionHash: response.hash,
		});

		// Send Sentry data asynchronously after transaction
		Sentry.startSpan(
			{
				op: 'transaction.withdraw',
				name: 'withdrawPosition',
			},
			(span) => {
				span.setAttribute('posId', posId);
				span.setAttribute('txHash', response.hash);
				span.setAttribute('success', executedTransaction.success);
			},
		);

		return executedTransaction.hash;
	} catch (error: any) {
		const parsedError = parseVaultError(error);
		Sentry.captureException(error, {
			tags: { operation: 'withdrawPosition' },
			contexts: {
				transaction: {
					posId,
					message: error?.message,
					status: error?.status,
					statusText: error?.statusText,
					parsedError,
				},
			},
		});
		throw new Error(parsedError);
	}
};

/**
 * Cancel position before agent processes using wallet adapter
 */
export const cancelPosition = async (
	posId: number,
	signAndSubmitFn: (payload: any) => Promise<{ hash: string }>,
	vaultConfig?: Partial<VaultConfig>,
): Promise<string> => {
	const config = resolveVaultConfig(vaultConfig);
	try {
		// console.log('🚀 [Safari Fix] About to cancel - popup should open NOW');

		const transaction = {
			data: {
				function:
					`${config.CONTRACT_ADDRESS}::${config.MODULE_NAME}::cancel_before_agent` as `${string}::${string}::${string}`,
				typeArguments: [],
				functionArguments: [posId.toString()],
			},
		};

		// CRITICAL: Direct call with no Sentry wrapper to avoid async delays
		const response = await signAndSubmitFn(transaction);
		/* console.log(
			'✅ [Safari Fix] Cancel transaction submitted:',
			response.hash,
		);*/

		const executedTransaction = await aptosClient.waitForTransaction({
			transactionHash: response.hash,
		});

		// Send Sentry data asynchronously after transaction
		Sentry.startSpan(
			{
				op: 'transaction.cancel',
				name: 'cancelPosition',
			},
			(span) => {
				span.setAttribute('posId', posId);
				span.setAttribute('txHash', response.hash);
				span.setAttribute('success', executedTransaction.success);
			},
		);

		return executedTransaction.hash;
	} catch (error: any) {
		const parsedError = parseVaultError(error);
		Sentry.captureException(error, {
			tags: { operation: 'cancelPosition' },
			contexts: {
				transaction: {
					posId,
					message: error?.message,
					status: error?.status,
					statusText: error?.statusText,
					parsedError,
				},
			},
		});
		throw new Error(parsedError);
	}
};
